import { Popover, Transition } from '@headlessui/react';
import clsx from 'clsx';
import {
  Fragment,
  PropsWithChildren,
  ReactElement,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useReducer,
  useRef,
  useState,
} from 'react';
import { CgClose } from 'react-icons/cg';
import { HiArrowRight } from 'react-icons/hi';

import { Button } from 'components/elements/button';
import Maybe from 'components/elements/maybe';
import { useGlobalStore } from 'lib/DataStore';
import { isBrowser } from 'lib/hooks/useIsomorphicLayoutEffect';
import { useOutsideClickDetector } from 'lib/hooks/useOutsideClickDetector';
import { useOwnTranslate } from 'lib/hooks/useOwnTranslate';
import { observer } from 'mobx-react';

interface Props extends PropsWithChildren {
  title: string | ReactElement;
  titleText?: string;
  active?: boolean;
  open?: boolean;
  onClose?: () => void;
  onClick?: (_open: boolean) => void;
  alwaysShowPanel?: boolean;
  clear?: () => void;
}

type FilterBoxReducerState = {
  open: boolean;
  filterFocuse: string;
};

type FilterBoxAction = {
  type: 'open' | 'close';
  payload?: Partial<FilterBoxReducerState>;
};

export type FilterBoxDropDownRef = {
  openHandler: (_filterFocuse?: string) => void;
  closeHandler: () => void;
  open: boolean;
};

const mobileFilterBoxReducer = (
  state: FilterBoxReducerState,
  action: FilterBoxAction
): FilterBoxReducerState => {
  switch (action.type) {
    case 'open':
      return {
        ...state,
        ...action.payload,
        open: true,
      };

    default:
      return {
        filterFocuse: '',
        open: false,
      };
  }
};

const FilterBoxItemDropDown = forwardRef<FilterBoxDropDownRef, Props>(
  (
    {
      title,
      active = false,
      open = false,
      children,
      alwaysShowPanel = true,
      onClose,
      titleText,
      onClick,
      clear,
    },
    ref
  ) => {
    const { userDevice } = useGlobalStore();
    const { t } = useOwnTranslate('plp');
    const panelRef = useRef<HTMLDivElement>(null);

    const [state, dispatch] = useReducer(mobileFilterBoxReducer, {
      filterFocuse: '',
      open: false,
    });

    const openFilterBox = useCallback(
      (filterFocuse = '') => {
        dispatch({
          type: 'open',
          payload: {
            filterFocuse: filterFocuse ? `mobileFilterbox-${filterFocuse}` : '',
          },
        });
        if (userDevice === 'MOBILE' && alwaysShowPanel) {
          document
            .querySelector('#navigation-bar')
            ?.classList.remove('sticky', 'z-20');
          setTimeout(() => {
            document.querySelector('#mobile-navigation')?.classList.add('!z-0');
          }, 100);
          document.body.classList.add('overflow-y-hidden');
        }
      },
      [state.open, state.filterFocuse]
    );

    const closeFilterBox = useCallback(() => {
      if (state.open) {
        dispatch({ type: 'close' });
        if (userDevice === 'MOBILE' && alwaysShowPanel) {
          document
            .querySelector('#navigation-bar')
            ?.classList.add('sticky', 'z-20');
          document
            .querySelector('#mobile-navigation')
            ?.classList.remove('!z-0');
          document.body.classList.remove('overflow-y-hidden');
        }
        if (onClose) onClose();
      }
    }, [state.open, state.filterFocuse]);

    const dorpDownPanelRef = useOutsideClickDetector<HTMLDivElement>(() => {
      closeFilterBox();
    });
    useImperativeHandle(ref, () => ({
      open: state.open,
      openHandler(_filterFocuse) {
        openFilterBox(_filterFocuse);
      },
      closeHandler() {
        closeFilterBox();
      },
      elementRef: dorpDownPanelRef.current,
    }));

    const mainRef = useRef<HTMLDivElement>(null);
    const toggleDropDown = useCallback(() => {
      if (state.open) {
        closeFilterBox();
      } else {
        openFilterBox();
      }

      if (onClick) onClick(state.open);
    }, [state.open]);

    useEffect(() => {
      if (open) {
        openFilterBox();
      }
    }, [open]);

    useEffect(() => {
      if (isBrowser()) {
        if (state.open && state.filterFocuse) {
          setTimeout(() => {
            const top = document
              .getElementById(state.filterFocuse)
              ?.getBoundingClientRect().top;

            if (top) {
              panelRef.current?.scrollTo({
                top: top - 50,
                behavior: 'smooth',
              });
            }
          }, 200);
        }
      }
    }, [state.filterFocuse]);

    const btn = useRef<HTMLButtonElement>(null);
    const [dropdownLeft, setDropdownLeft] = useState(0);
    useEffect(() => {
      if (state.open) {
        if (mainRef.current) {
          const parent = mainRef.current.closest('#filter-box');
          if (parent) {
            setDropdownLeft(
              Math.abs(
                mainRef.current.getBoundingClientRect().right -
                parent.getBoundingClientRect().right
              )
            );
          }
        }
      }
    }, [state.open]);

    return (
      <div className="scroll-mt-6 w-max" ref={mainRef}>
        <Popover ref={dorpDownPanelRef}>
          <Popover.Button
            as="button"
            ref={btn}
            onClick={() => toggleDropDown()}
            className={clsx(
              'flex items-center px-4 py-2 space-x-4 rtl:space-x-reverse border rounded-md  active:bg-primary-25 min-w-max  active:text-primary-700 active:border-primary-25 focus-visible:outline-none',
              {
                'bg-primary-25 text-primary-700 border-primary-25': active,
                'text-grey-700 border-grey-300': !active,
              }
            )}
          >
            {title}
          </Popover.Button>

          <Maybe condition={alwaysShowPanel}>
            <Transition
              static
              enter="transition duration-200 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-100 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
              as={Fragment}
              show={state.open}
            >
              <Popover.Panel
                as="div"
                ref={panelRef}
                style={
                  userDevice === 'DESKTOP'
                    ? {
                      right: dropdownLeft,
                    }
                    : {}
                }
                className={clsx(
                  'bg-white min-w-max overflow-y-auto z-[10000] rounded-md scroll-mt-10 overflow-x-hidden',
                  {
                    'fixed top-0 right-0 left-0 bottom-0':
                      userDevice === 'MOBILE',
                    'absolute w-48 mt-2 border shadow-lg border-grey-300 max-h-[400px]':
                      userDevice !== 'MOBILE',
                    // [`right-[${Math.ceil(
                    //   mainRef.current?.getBoundingClientRect().left || 0
                    // )}px] right-[173px]`]: userDevice !== 'MOBILE',
                  }
                )}
              >
                <Maybe condition={userDevice === 'MOBILE'}>
                  <div className="absolute flex flex-col w-full py-0 scroll-mt-6">
                    <div className="sticky top-0 z-10 flex h-12 bg-white">
                      <div className="flex items-center justify-start flex-1">
                        <span
                          className="w-1/2 p-2 text-xl text-primary-500"
                          onClick={closeFilterBox}
                        >
                          <HiArrowRight className="w-6 h-6" />
                        </span>
                      </div>
                      <div className="flex items-center justify-center flex-1">
                        {t('allFilters')}
                      </div>
                      <div className="flex items-center justify-center flex-1" />
                    </div>
                    <div>{children}</div>
                    <div className="sticky bottom-0 flex p-4 bg-white gap-3">
                      <Button variant="light" onClick={() => {if (clear) clear();}} style={{maxWidth:"50%"}}>
                        پاک کردن همه
                      </Button>
                      <Button variant="primary" onClick={() => closeFilterBox()} style={{maxWidth:"50%"}}>
                        انجام شود
                      </Button>
                    </div>
                  </div>
                </Maybe>
                <Maybe condition={userDevice !== 'MOBILE'}>
                  <>
                    <div className="flex items-center px-6 py-3">
                      <span className="flex-1 font-bold justify-self-start">
                        {titleText}
                      </span>
                      <span className="flex justify-end font-bold cursor-pointer" onClick={closeFilterBox}>
                        <CgClose className="w-5 h-5" />
                      </span>
                    </div>
                    {children}
                  </>
                </Maybe>
              </Popover.Panel>
            </Transition>
          </Maybe>
        </Popover>
      </div>
    );
  }
);

FilterBoxItemDropDown.displayName = 'FilterBoxItemDropDown';

export default observer(FilterBoxItemDropDown);
